import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Fade,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  Modal,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  TextField,
  Tooltip,
  Typography,
  Drawer,
  Backdrop
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import WarehouseOutlinedIcon from "@mui/icons-material/WarehouseOutlined";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import AccessController from "_shared/AccessController";
import { DashGridL2 } from "layouts/dash-grid";
import TopBar from "_shared/Top";
import { Toaster } from "react-hot-toast";
import { toastMessage } from "utils/toastMessage";
import { PageHeader, StatCard, EmptyState, TablePager } from "_shared/ui";
import {
  getAllSEUsers,
  checkDepotCode,
  addDepotToSE,
  editDepotOnSE,
  deleteDepotFromSE,
  deleteTerritoryFromSE,
  getSEProfile,
  updateSEProfile,
  verifySESync,
  syncSEUser,
  verifySOTerritory,
  assignSOTerritory
} from "_controllers/officer";

const modalBackdropSx = {
  backdropFilter: "blur(3px)",
  bgcolor: (theme) => alpha(theme.palette.common.black, 0.5),
};

const modalStyle = {
  width: { xs: "92%", sm: "520px", md: "560px" },
  maxWidth: "96vw",
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  border: "1px solid",
  borderColor: "divider",
  boxShadow: 24,
  outline: "none",
  overflow: "hidden",
};

const largeModalStyle = {
  ...modalStyle,
  width: { xs: "96%", sm: "90%", md: "78%", lg: "74%" },
  maxHeight: "90vh",
  display: "flex",
  flexDirection: "column",
};

const formModalStyle = {
  position: "absolute",
  outline: "none",
  bgcolor: "background.paper",
  border: "1px solid",
  borderColor: "divider",
  boxShadow: 24,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  width: { xs: "100%", sm: "600px", md: "640px" },
  maxWidth: { xs: "100vw", sm: "96vw" },
  maxHeight: { xs: "100dvh", sm: "92vh" },
  top: { xs: 0, sm: "50%" },
  left: { xs: 0, sm: "50%" },
  right: { xs: 0, sm: "auto" },
  bottom: { xs: 0, sm: "auto" },
  transform: { xs: "none", sm: "translate(-50%, -50%)" },
};

// Subtle theme-aware header band (mode-aware; reads as a layer above paper).
const headerBandSx = { bgcolor: "background.default" };

const getInitials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("") || "?";

const KPI_ACCENTS = {
  primary: "primary",
  success: "success",
  info: "info",
  warning: "warning",
};

const KpiCard = ({ label, value, icon: Icon, accent = "primary", subtitle, compact = false }) => {
  const color = KPI_ACCENTS[accent] ?? "primary";

  return (
    <Paper
      elevation={0}
      sx={{
        p: compact ? 1.5 : 2,
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        borderLeft: "4px solid",
        borderLeftColor: `${color}.main`,
        height: "100%",
        transition: "all 0.22s ease",
        cursor: "default",
        "&:hover": {
          boxShadow: 6,
          transform: "translateY(-2px)",
          borderColor: "divider",
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={compact ? 1.25 : 1.75}>
        {Icon && (
          <Box
            sx={{
              p: compact ? 0.85 : 1.15,
              bgcolor: (theme) => alpha(theme.palette[color].main, 0.1),
              color: `${color}.main`,
              display: "flex",
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: compact ? 20 : 26 }} />
          </Box>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: 0.4,
              fontSize: compact ? "0.65rem" : "0.7rem",
              lineHeight: 1.3,
            }}
          >
            {label}
          </Typography>
          <Typography
            variant={compact ? "h6" : "h4"}
            color="text.primary"
            sx={{ fontWeight: 700, lineHeight: 1.15, mt: 0.25 }}
            noWrap={compact}
            title={String(value)}
          >
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.35, lineHeight: 1.3 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Stack>
    </Paper>
  );
};

const DepotAssignmentCard = ({ depot, onEdit, onDelete }) => (
  <Card
    elevation={0}
    sx={{
      height: "100%",
      border: "1px solid",
      borderColor: "divider",
      transition: "all 0.22s ease",
      "&:hover": {
        borderColor: "primary.light",
        boxShadow: 6,
        transform: "translateY(-2px)",
      },
    }}
  >
    <CardContent sx={{ pb: 1 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        <Chip
          label={depot.depotCode}
          size="small"
          color="primary"
          sx={{ fontWeight: 700 }}
        />
        <WarehouseOutlinedIcon fontSize="small" color="action" />
      </Stack>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1.25, mb: 0.5 }} noWrap title={depot.depotName}>
        {depot.depotName}
      </Typography>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        <Chip label={depot.regionCode} size="small" variant="outlined" sx={{ height: 22, fontSize: "0.7rem" }} />
        <Chip
          label={depot.regionName}
          size="small"
          variant="outlined"
          sx={{ height: 22, fontSize: "0.7rem", maxWidth: "100%" }}
        />
      </Stack>
    </CardContent>
    <CardActions sx={{ px: 2, pb: 2, pt: 0, gap: 0.5 }}>
      <Button
        size="small"
        variant="outlined"
        color="primary"
        startIcon={<EditOutlinedIcon />}
        onClick={() => onEdit(depot)}
        sx={{ flex: 1, textTransform: "none" }}
      >
        Edit
      </Button>
      <Button
        size="small"
        variant="outlined"
        color="error"
        startIcon={<DeleteIcon />}
        onClick={() => onDelete(depot)}
        sx={{ flex: 1, textTransform: "none" }}
      >
        Remove
      </Button>
    </CardActions>
  </Card>
);

const ManageDepotsModal = ({
  open,
  onClose,
  selectedSE,
  onAddDepot,
  onEditDepot,
  onDeleteDepot,
}) => {
  const [depotSearch, setDepotSearch] = useState("");

  useEffect(() => {
    if (!open) setDepotSearch("");
  }, [open]);

  const depots = selectedSE?.depots ?? [];
  const filteredDepots = useMemo(() => {
    const q = depotSearch.trim().toLowerCase();
    if (!q) return depots;
    return depots.filter(
      (d) =>
        d.depotCode?.toLowerCase().includes(q) ||
        d.depotName?.toLowerCase().includes(q) ||
        d.regionCode?.toLowerCase().includes(q) ||
        d.regionName?.toLowerCase().includes(q)
    );
  }, [depots, depotSearch]);

  const uniqueRegions = useMemo(
    () => new Set(depots.map((d) => d.regionCode).filter(Boolean)).size,
    [depots]
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      BackdropProps={{ sx: modalBackdropSx }}
    >
      <Fade in={open} timeout={280}>
        <Box sx={largeModalStyle}>
          <Box
            sx={{
              px: { xs: 2, sm: 3 },
              py: 2,
              borderBottom: "1px solid",
              borderColor: "divider",
              ...headerBandSx,
            }}
          >
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} sx={{ flex: 1 }}>
                <Avatar
                  sx={{
                    width: 52,
                    height: 52,
                    fontWeight: 700,
                    fontSize: "1rem",
                    bgcolor: "primary.main",
                    border: "2px solid",
                    borderColor: "background.paper",
                    boxShadow: 1,
                  }}
                >
                  {getInitials(selectedSE?.name)}
                </Avatar>
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <Box
                      sx={{
                        p: 0.75,
                        bgcolor: "background.paper",
                        color: "primary.main",
                        border: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                      }}
                    >
                      <WarehouseOutlinedIcon fontSize="small" />
                    </Box>
                    <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
                      Manage Depots
                    </Typography>
                  </Stack>
                  <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3, color: "text.primary" }}>
                    {selectedSE?.name ?? "Sales Executive"}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                    <Chip size="small" label={`SAP ${selectedSE?.SAP_ID ?? "—"}`} variant="outlined" />
                    <Chip
                      size="small"
                      label={selectedSE?.isActive ? "Active" : "Inactive"}
                      color={selectedSE?.isActive ? "success" : "default"}
                    />
                  </Stack>
                </Box>
              </Stack>
              <IconButton size="small" onClick={onClose} aria-label="Close">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>

            <Grid container spacing={1.5} sx={{ mt: 2 }}>
              <Grid item xs={12} sm={4}>
                <KpiCard
                  compact
                  label="Assigned Depots"
                  value={depots.length}
                  icon={WarehouseOutlinedIcon}
                  accent="primary"
                  subtitle={`${depots.length} depot${depots.length === 1 ? "" : "s"} linked to this SE`}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <KpiCard
                  compact
                  label="Unique Regions"
                  value={uniqueRegions}
                  icon={PlaceOutlinedIcon}
                  accent="info"
                  subtitle="Across assigned depots"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <KpiCard
                  compact
                  label="SE Home Region"
                  value={selectedSE?.regionCode ?? "—"}
                  icon={ManageAccountsOutlinedIcon}
                  accent="warning"
                  subtitle={selectedSE?.regionName ?? "Region not set"}
                />
              </Grid>
            </Grid>
          </Box>

          <Box
            sx={{
              px: { xs: 2, sm: 3 },
              py: 1.5,
              bgcolor: "background.default",
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              alignItems={{ sm: "center" }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <EmailOutlinedIcon fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary">
                    {selectedSE?.email ?? "—"}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <PhoneOutlinedIcon fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary">
                    {selectedSE?.phone ?? "—"}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <PlaceOutlinedIcon fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary">
                    {selectedSE?.regionName ?? "—"}
                  </Typography>
                </Stack>
              </Stack>
              <TextField
                size="small"
                placeholder="Search depots in this list..."
                value={depotSearch}
                onChange={(e) => setDepotSearch(e.target.value)}
                sx={{ minWidth: { xs: "100%", sm: 260 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
          </Box>

          <Box sx={{ ...modalBodySx, bgcolor: "background.paper" }}>
            {depots.length === 0 ? (
              <Paper
                variant="outlined"
                sx={{
                  py: 6,
                  px: 3,
                  textAlign: "center",
                  borderStyle: "dashed",
                  bgcolor: "background.paper",
                }}
              >
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: "50%",
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 2,
                  }}
                >
                  <WarehouseOutlinedIcon sx={{ fontSize: 36, color: "primary.main" }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                  No depots assigned yet
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, maxWidth: 360, mx: "auto" }}>
                  Start by adding the first depot for this Sales Executive. You can assign multiple depots across
                  regions.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={onAddDepot}
                  sx={{ textTransform: "none", px: 3 }}
                >
                  Add First Depot
                </Button>
              </Paper>
            ) : filteredDepots.length === 0 ? (
              <Alert severity="info">
                No depots match &quot;{depotSearch}&quot;. Try a different search term.
              </Alert>
            ) : (
              <Grid container spacing={2}>
                {filteredDepots.map((depot) => (
                  <Grid item xs={12} sm={6} lg={4} key={depot.depotCode}>
                    <DepotAssignmentCard depot={depot} onEdit={onEditDepot} onDelete={onDeleteDepot} />
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>

          <Box sx={{ ...modalFooterSx, justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center" }}>
              {filteredDepots.length} of {depots.length} depot(s) shown
            </Typography>
            <Stack direction="row" spacing={1.5}>
              <Button variant="outlined" color="inherit" onClick={onClose} sx={{ textTransform: "none" }}>
                Close
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={onAddDepot}
                sx={{ textTransform: "none", px: 2.5 }}
              >
                Add Depot
              </Button>
            </Stack>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

const modalBodySx = {
  px: { xs: 2, sm: 3 },
  py: { xs: 2, sm: 2.5 },
  overflowY: "auto",
  flex: 1,
  minHeight: 0,
  WebkitOverflowScrolling: "touch",
};

const modalFooterSx = {
  px: { xs: 2, sm: 3 },
  py: 2,
  borderTop: "1px solid",
  borderColor: "divider",
  bgcolor: "background.default",
  display: "flex",
  justifyContent: "flex-end",
  gap: 1.5,
};

const emptyDepotForm = { depotCode: "", depotName: "", regionCode: "", regionName: "" };
const emptyErrors = { depotCode: "", depotName: "", regionCode: "", regionName: "" };
const emptyProfileForm = { email: "", phone: "", regionCode: "" };
const emptyProfileErrors = { email: "", phone: "", regionCode: "" };

const emptyAddSEForm = {
  SAP_ID: "",
  name: "",
  email: "",
  phone: "",
  regionCode: "",
  regionName: "",
  isActive: true,
};
const emptyAddSEErrors = {
  SAP_ID: "",
  name: "",
  email: "",
  phone: "",
  regionCode: "",
  regionName: "",
};

const FormSection = ({ title, subtitle, icon: Icon, children }) => (
  <Paper
    variant="outlined"
    sx={{
      p: { xs: 1.5, sm: 2.25 },
      bgcolor: "background.paper",
      borderColor: "divider",
      overflow: "hidden",
    }}
  >
    <Stack
      direction="row"
      spacing={1.25}
      alignItems="flex-start"
      sx={{ mb: { xs: 1.5, sm: 2 } }}
    >
      {Icon && (
        <Box
          sx={{
            p: 0.85,
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
            color: "primary.main",
            display: "flex",
            flexShrink: 0,
          }}
        >
          <Icon fontSize="small" />
        </Box>
      )}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Stack>
    <Box sx={{ width: "100%" }}>{children}</Box>
  </Paper>
);

const AddSEModal = ({ open, onClose, onRefresh }) => {
  const [sapId, setSapId] = useState("");
  const [statusMsg, setStatusMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [sapData, setSapData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setSapId("");
      setStatusMsg("");
      setErrorMsg("");
      setSapData(null);
      setLoading(false);
    }
  }, [open]);

  const handleVerify = async () => {
    if (!sapId.trim()) {
      setErrorMsg("Please enter an SAP ID");
      return;
    }
    setLoading(true);
    setErrorMsg("");
    setStatusMsg("");
    setSapData(null);
    try {
      const res = await verifySESync({ sapId: sapId.trim() });
      if (res.status === "exists") {
        setErrorMsg(res.message);
      } else if (res.status === "not_found") {
        setErrorMsg(res.message);
      } else if (res.status === "sap_found") {
        setSapData(res.sapData);
        setStatusMsg(res.message);
      } else {
        setErrorMsg(res.message || "Unknown error");
      }
    } catch (err) {
      setErrorMsg(err?.message || "Failed to verify");
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setLoading(true);
    try {
      const res = await syncSEUser({ sapId: sapId.trim() });
      if (res.status === "success") {
        toastMessage("SE Synced successfully!", "success");
        if (onRefresh) onRefresh();
        onClose();
      } else {
        setErrorMsg(res.message);
      }
    } catch (err) {
      setErrorMsg(err?.message || "Failed to sync SE");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      BackdropProps={{ sx: modalBackdropSx }}
    >
      <Fade in={open}>
        <Box sx={formModalStyle}>
          <ModalHeader
            icon={PersonAddOutlinedIcon}
            title="Sync SE from SAP"
            subtitle="Verify and sync an SE from the SAP backend"
            onClose={onClose}
            accent="primary.main"
          />
          <Box sx={{ ...modalBodySx, bgcolor: "background.default" }}>
            <Stack spacing={2.5}>
              <FormSection title="SAP Details" subtitle="Enter the SAP ID of the Service Engineer">
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <TextField
                    fullWidth
                    size="small"
                    label="SAP ID"
                    required
                    value={sapId}
                    onChange={(e) => setSapId(e.target.value)}
                    disabled={loading || !!sapData}
                  />
                  <Button
                    variant="contained"
                    disabled={loading || !!sapData}
                    onClick={handleVerify}
                    sx={{ textTransform: "none", height: 40, px: 3 }}
                  >
                    {loading && !sapData ? <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} /> : null}
                    Verify
                  </Button>
                </Stack>
                {errorMsg && <Alert severity="error" sx={{ mt: 2 }}>{errorMsg}</Alert>}
                {statusMsg && <Alert severity="success" sx={{ mt: 2 }}>{statusMsg}</Alert>}
              </FormSection>

              {sapData && (
                <FormSection title="SAP Response" subtitle="Details fetched from SAP">
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <ProfileDetailRow label="Name" value={sapData.Name || 'N/A'} />
                      <ProfileDetailRow label="Email" value={sapData.EmailId || 'N/A'} />
                      <ProfileDetailRow label="Cell" value={sapData.Cell || 'N/A'} />
                      <ProfileDetailRow label="Status" value={sapData.Status === "A" ? "Active" : sapData.Status || 'N/A'} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <ProfileDetailRow label="Zone" value={`${sapData.PZoneTxt || ''} (${sapData.PZone || 'N/A'})`} />
                      <ProfileDetailRow label="Region" value={`${sapData.RegioTxt || ''} (${sapData.Regio || 'N/A'})`} />
                      <ProfileDetailRow label="Area" value={`${sapData.SeTerrGrpTxt || ''} (${sapData.Area || 'N/A'})`} />
                      <ProfileDetailRow label="Bzirk" value={sapData.Bzirk} />
                    </Grid>
                  </Grid>
                </FormSection>
              )}
            </Stack>
          </Box>
          <Box sx={{ ...modalFooterSx }}>
            <Button variant="outlined" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            {sapData && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleSync}
                disabled={loading}
                startIcon={<SaveOutlinedIcon />}
              >
                Sync to Master
              </Button>
            )}
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

const AssignTerritoryModal = ({ open, onClose, seUser, onRefresh }) => {
  const [soSapId, setSoSapId] = useState("");
  const [statusMsg, setStatusMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [sapData, setSapData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setSoSapId("");
      setStatusMsg("");
      setErrorMsg("");
      setSapData(null);
      setLoading(false);
    }
  }, [open]);

  const handleVerify = async () => {
    if (!soSapId.trim()) {
      setErrorMsg("Please enter an SO SAP ID");
      return;
    }
    if (!seUser?.SAP_ID) {
      setErrorMsg("SE User SAP ID is missing");
      return;
    }
    setLoading(true);
    setErrorMsg("");
    setStatusMsg("");
    setSapData(null);
    try {
      const res = await verifySOTerritory({ soSapId: soSapId.trim(), seSapId: seUser.SAP_ID });
      if (res.status === "assigned") {
        setErrorMsg(res.message);
      } else if (res.status === "not_found") {
        setErrorMsg(res.message);
      } else if (res.status === "sap_found") {
        setSapData(res.sapData);
        setStatusMsg(res.message);
      } else {
        setErrorMsg(res.message || "Unknown error");
      }
    } catch (err) {
      setErrorMsg(err?.message || "Failed to verify territory");
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    setLoading(true);
    try {
      const res = await assignSOTerritory({ soSapId: soSapId.trim(), seSapId: seUser.SAP_ID });
      if (res.status === "success") {
        toastMessage("Territory assigned successfully!", "success");
        if (onRefresh) onRefresh();
        onClose();
      } else {
        setErrorMsg(res.message);
      }
    } catch (err) {
      setErrorMsg(err?.message || "Failed to assign territory");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition BackdropProps={{ sx: modalBackdropSx }}>
      <Fade in={open}>
        <Box sx={formModalStyle}>
          <ModalHeader icon={PlaceOutlinedIcon} title="Assign SO Territory" subtitle={`Assign SO Territory to SE: ${seUser?.name} (${seUser?.SAP_ID})`} onClose={onClose} accent="info.main" />
          <Box sx={{ ...modalBodySx, bgcolor: "background.default" }}>
            <Stack spacing={2.5}>
              <FormSection title="SO Territory Details" subtitle="Enter the SAP ID of the Sales Officer (SO)">
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <TextField fullWidth size="small" label="SO SAP ID" required value={soSapId} onChange={(e) => setSoSapId(e.target.value)} disabled={loading || !!sapData} />
                  <Button variant="contained" disabled={loading || !!sapData} onClick={handleVerify} sx={{ textTransform: "none", height: 40, px: 3 }}>
                    {loading && !sapData ? <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} /> : null}
                    Verify
                  </Button>
                </Stack>
                {errorMsg && <Alert severity="error" sx={{ mt: 2 }}>{errorMsg}</Alert>}
                {statusMsg && <Alert severity="success" sx={{ mt: 2 }}>{statusMsg}</Alert>}
              </FormSection>

              {sapData && (
                <FormSection title="SAP Territory Data" subtitle="Territory details fetched from SAP">
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <ProfileDetailRow label="Territory Name" value={sapData.Name} />
                      <ProfileDetailRow label="Territory Code" value={sapData.Bzirk} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <ProfileDetailRow label="Position" value={sapData.TerrPosition} />
                      <ProfileDetailRow label="Category" value={sapData.TerrCat} />
                    </Grid>
                  </Grid>
                </FormSection>
              )}
            </Stack>
          </Box>
          <Box sx={{ ...modalFooterSx }}>
            <Button variant="outlined" onClick={onClose} disabled={loading}>Cancel</Button>
            {sapData && (
              <Button variant="contained" color="primary" onClick={handleAssign} disabled={loading} startIcon={<SaveOutlinedIcon />}>
                Assign Territory
              </Button>
            )}
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

const ProfileDetailRow = ({ icon: Icon, label, value }) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ py: 0.75 }}>
    {Icon && <Icon fontSize="small" color="action" sx={{ mt: 0.35 }} />}
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.2 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-word" }}>
        {value || "—"}
      </Typography>
    </Box>
  </Stack>
);

const SEProfileModal = ({ open, onClose, se, onUpdated }) => {
  const [editMode, setEditMode] = useState(false);
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(emptyProfileForm);
  const [formErrors, setFormErrors] = useState(emptyProfileErrors);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const resetFormFromProfile = (data) => {
    setForm({
      email: data?.email ?? "",
      phone: data?.phone ?? "",
      regionCode: data?.regionCode ?? "",
    });
    setFormErrors(emptyProfileErrors);
  };

  useEffect(() => {
    if (!open || !se) return;

    const load = async () => {
      setEditMode(false);
      setLoading(true);
      try {
        const res = await getSEProfile(se.SAP_ID);
        const data = res?.data ?? res;
        const merged = { ...se, ...data };
        setProfile(merged);
        resetFormFromProfile(merged);
      } catch {
        setProfile(se);
        resetFormFromProfile(se);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [open, se]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateProfile = () => {
    const errs = {};
    if (!form.phone.trim()) {
      errs.phone = "Phone is required";
    } else if (!/^\d{10}$/.test(form.phone.trim())) {
      errs.phone = "Phone must be exactly 10 digits";
    }

    if (!form.email.trim()) {
      errs.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      errs.email = "Enter a valid email";
    }

    if (!form.regionCode.trim()) {
      errs.regionCode = "Region code is required";
    } else if (form.regionCode.trim().length < 2) {
      errs.regionCode = "Region code must be at least 2 characters";
    }

    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validateProfile() || !profile) return;
    setSaveLoading(true);
    try {
      const payload = { seSapId: profile.SAP_ID };
      if (form.email !== profile.email) payload.email = form.email.trim();
      if (form.phone !== profile.phone) payload.phone = form.phone.trim();
      if (form.regionCode !== profile.regionCode) payload.regionCode = form.regionCode.trim();

      if (Object.keys(payload).length === 1) {
        toastMessage("No changes to save", "info");
        setEditMode(false);
        return;
      }

      const res = await updateSEProfile(payload);
      if (res?.status === "success") {
        const updated = { ...profile, ...form };
        setProfile(updated);
        resetFormFromProfile(updated);
        setEditMode(false);
        onUpdated(updated);
        toastMessage(res.message ?? "Profile updated successfully", "success");
      } else {
        toastMessage(res?.message ?? "Error updating profile", "error");
      }
    } catch (err) {
      toastMessage(err?.response?.data?.message ?? err?.message ?? "Failed to update profile", "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleClose = () => {
    setEditMode(false);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeAfterTransition
      BackdropProps={{ sx: modalBackdropSx }}
    >
      <Fade in={open}>
        <Box sx={{ ...modalStyle, width: { xs: "92%", sm: "520px", md: "540px" } }}>
          <ModalHeader
            icon={PersonOutlineIcon}
            title="SE Profile"
            subtitle={profile ? `SAP ${profile.SAP_ID}` : "Sales Executive details"}
            onClose={handleClose}
          />
          <Box sx={modalBodySx}>
            {loading ? (
              <Stack spacing={1.5}>
                <Skeleton variant="rounded" height={72} />
                <Skeleton variant="rounded" height={180} />
              </Stack>
            ) : (
              <>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    mb: 2.5,
                    ...headerBandSx,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <Avatar sx={{ width: 52, height: 52, bgcolor: "primary.main", fontWeight: 700 }}>
                    <PersonOutlineIcon />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
                      Service Engineer
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      SAP ID: {profile?.SAP_ID}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
                      <Chip
                        size="small"
                        label={profile?.isActive ? "Active" : "Inactive"}
                        color={profile?.isActive ? "success" : "default"}
                      />
                      <Chip
                        size="small"
                        label={`${profile?.depots?.length ?? 0} depot(s)`}
                        variant="outlined"
                        color="primary"
                      />
                    </Stack>
                  </Box>
                </Paper>

                {editMode ? (
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="SAP ID"
                      value={profile?.SAP_ID ?? ""}
                      disabled
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Email"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleFormChange}
                      error={!!formErrors.email}
                      helperText={formErrors.email}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Phone"
                      name="phone"
                      value={form.phone}
                      onChange={handleFormChange}
                      error={!!formErrors.phone}
                      helperText={formErrors.phone}
                    />
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={12}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Region Code"
                          name="regionCode"
                          value={form.regionCode}
                          onChange={handleFormChange}
                          error={!!formErrors.regionCode}
                          helperText={formErrors.regionCode}
                        />
                      </Grid>
                    </Grid>
                  </Stack>
                ) : (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <ProfileDetailRow icon={EmailOutlinedIcon} label="Email" value={profile?.email || "N/A"} />
                    <Divider />
                    <ProfileDetailRow icon={PhoneOutlinedIcon} label="Phone" value={profile?.phone || "N/A"} />
                    <Divider />
                    <ProfileDetailRow
                      icon={PlaceOutlinedIcon}
                      label="Region"
                      value={profile?.regionCode || "N/A"}
                    />
                    <Divider />
                    <ProfileDetailRow
                      icon={PlaceOutlinedIcon}
                      label="Area"
                      value={
                        profile?.areaName || profile?.areaCode
                          ? `${profile?.areaName ?? "N/A"}${profile?.areaCode ? ` (${profile.areaCode})` : ""}`
                          : "N/A"
                      }
                    />
                    <Divider />
                    <ProfileDetailRow
                      icon={PlaceOutlinedIcon}
                      label="Zone"
                      value={profile?.zone || "N/A"}
                    />
                    <Divider />
                    <ProfileDetailRow
                      icon={PersonOutlineIcon}
                      label="Role"
                      value={profile?.role || "N/A"}
                    />
                  </Paper>
                )}
              </>
            )}
          </Box>
          <Box sx={{ ...modalFooterSx, justifyContent: "space-between" }}>
            <Button
              variant="text"
              color="inherit"
              onClick={handleClose}
              sx={{ textTransform: "none" }}
            >
              Close
            </Button>
            <Stack direction="row" spacing={1}>
              {editMode ? (
                <>
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={() => {
                      resetFormFromProfile(profile);
                      setEditMode(false);
                    }}
                    sx={{ textTransform: "none" }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={saveLoading ? null : <SaveOutlinedIcon />}
                    onClick={handleSave}
                    disabled={saveLoading || loading}
                    sx={{ textTransform: "none", minWidth: 130 }}
                  >
                    {saveLoading ? <CircularProgress size={20} color="inherit" /> : "Save Changes"}
                  </Button>
                </>
              ) : (
                <Button
                  variant="contained"
                  startIcon={<EditOutlinedIcon />}
                  onClick={() => setEditMode(true)}
                  disabled={loading}
                  sx={{ textTransform: "none" }}
                >
                  Edit Profile
                </Button>
              )}
            </Stack>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

const ModalHeader = ({ icon: Icon, title, subtitle, onClose, accent = "primary.main" }) => (
  <Box
    sx={{
      px: { xs: 2, sm: 3 },
      py: { xs: 1.5, sm: 2 },
      borderBottom: "1px solid",
      borderColor: "divider",
      ...headerBandSx,
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 1.5,
      flexShrink: 0,
    }}
  >
    <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ minWidth: 0, flex: 1 }}>
      {Icon && (
        <Box
          sx={{
            mt: 0.25,
            p: 1,
            bgcolor: "background.paper",
            color: accent,
            border: "1px solid",
            borderColor: "divider",
            display: "flex",
            flexShrink: 0,
          }}
        >
          <Icon fontSize="small" />
        </Box>
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, lineHeight: 1.3, fontSize: { xs: "1.05rem", sm: "1.25rem" } }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5, wordBreak: "break-word" }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
    </Stack>
    {onClose && (
      <IconButton
        size="small"
        onClick={onClose}
        aria-label="Close"
        sx={{ mt: -0.5, flexShrink: 0 }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    )}
  </Box>
);

const AssignedDepotsHover = ({ depots = [] }) => {
  const count = depots.length;

  const tooltipContent =
    count === 0 ? (
      <Stack alignItems="center" spacing={0.75} sx={{ px: 1, py: 0.5 }}>
        <WarehouseOutlinedIcon sx={{ fontSize: 28, color: "action.disabled" }} />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          No depots assigned
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Open Manage Depots to add assignments
        </Typography>
      </Stack>
    ) : (
      <Box sx={{ width: 300 }}>
        <Box
          sx={{
            px: 1.5,
            py: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
            ...headerBandSx,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <WarehouseOutlinedIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Assigned Depots
            </Typography>
            <Chip label={count} size="small" color="primary" sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700 }} />
          </Stack>
        </Box>
        <List dense disablePadding sx={{ maxHeight: 220, overflowY: "auto", py: 0.5 }}>
          {depots.map((depot, index) => (
            <ListItem
              key={depot.depotCode}
              disableGutters
              sx={{
                px: 1.5,
                py: 0.85,
                borderBottom: index < depots.length - 1 ? "1px solid" : "none",
                borderColor: "divider",
                transition: "background-color 0.15s ease",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ width: "100%" }}>
                <Chip
                  label={depot.depotCode}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ height: 24, fontWeight: 700, fontSize: "0.72rem", flexShrink: 0 }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: "block", lineHeight: 1.35 }} noWrap>
                    {depot.depotName}
                  </Typography>
                  {(depot.regionCode || depot.regionName) && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.35 }}>
                      {[depot.regionCode, depot.regionName].filter(Boolean).join(" · ")}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </ListItem>
          ))}
        </List>
      </Box>
    );

  return (
    <Tooltip
      title={tooltipContent}
      arrow
      placement="top"
      enterDelay={250}
      leaveDelay={120}
      describeChild
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: "background.paper",
            color: "text.primary",
            p: 0,
            border: "1px solid",
            borderColor: "divider",
            boxShadow: 8,
            maxWidth: 320,
            "& .MuiTooltip-arrow": {
              color: "background.paper",
              "&::before": {
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.paper",
              },
            },
          },
        },
      }}
    >
      <Chip
        component="span"
        icon={<WarehouseOutlinedIcon sx={{ fontSize: "16px !important" }} />}
        label={`${count} depot(s)`}
        size="small"
        color={count > 0 ? "primary" : "default"}
        variant={count > 0 ? "outlined" : "filled"}
        onClick={(e) => e.stopPropagation()}
        sx={{
          cursor: "pointer",
          fontWeight: 600,
          transition: "all 0.2s ease",
          "&:hover": {
            boxShadow: 4,
            borderColor: "primary.main",
            bgcolor: (theme) => (count > 0 ? alpha(theme.palette.primary.main, 0.06) : undefined),
          },
        }}
      />
    </Tooltip>
  );
};

const AssignedTerritoriesHover = ({ territories = [] }) => {
  const count = territories.length;

  const tooltipContent =
    count === 0 ? (
      <Stack alignItems="center" spacing={0.75} sx={{ px: 1, py: 0.5 }}>
        <PlaceOutlinedIcon sx={{ fontSize: 28, color: "action.disabled" }} />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          No territories assigned
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Click Assign SO Territory to add
        </Typography>
      </Stack>
    ) : (
      <Box sx={{ width: 300 }}>
        <Box
          sx={{
            px: 1.5,
            py: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
            ...headerBandSx,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <PlaceOutlinedIcon fontSize="small" color="info" />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Assigned Territories
            </Typography>
            <Chip label={count} size="small" color="info" sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700 }} />
          </Stack>
        </Box>
        <List dense disablePadding sx={{ maxHeight: 220, overflowY: "auto", py: 0.5 }}>
          {territories.map((terr, index) => (
            <ListItem
              key={terr.SAP_ID}
              disableGutters
              sx={{
                px: 1.5,
                py: 0.85,
                borderBottom: index < territories.length - 1 ? "1px solid" : "none",
                borderColor: "divider",
                transition: "background-color 0.15s ease",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ width: "100%" }}>
                <Chip
                  label={terr.SAP_ID}
                  size="small"
                  color="info"
                  variant="outlined"
                  sx={{ height: 24, fontWeight: 700, fontSize: "0.72rem", flexShrink: 0 }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: "block", lineHeight: 1.35 }} noWrap>
                    {terr.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.35 }}>
                    {[terr.territoryType, terr.territoryCode].filter(Boolean).join(" · ")}
                  </Typography>
                </Box>
              </Stack>
            </ListItem>
          ))}
        </List>
      </Box>
    );

  return (
    <Tooltip
      title={tooltipContent}
      arrow
      placement="top"
      enterDelay={250}
      leaveDelay={120}
      describeChild
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: "background.paper",
            color: "text.primary",
            boxShadow: 8,
            p: 0,
            border: "1px solid",
            borderColor: "divider",
          },
        },
        arrow: { sx: { color: "background.paper", "&::before": { border: "1px solid", borderColor: "divider" } } },
      }}
    >
      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
        {count === 0 ? (
          <Typography variant="body2" color="text.disabled" sx={{ fontStyle: "italic" }}>
            None
          </Typography>
        ) : (
          <>
            {territories.slice(0, 2).map((t) => (
              <Chip
                key={t.SAP_ID}
                label={t.SAP_ID}
                size="small"
                variant="outlined"
                color="info"
                sx={{ height: 22, fontSize: "0.7rem", fontWeight: 600, bgcolor: (theme) => alpha(theme.palette.info.main, 0.1) }}
              />
            ))}
            {count > 2 && (
              <Chip
                label={`+${count - 2}`}
                size="small"
                color="default"
                sx={{ height: 22, fontSize: "0.7rem", fontWeight: 600 }}
              />
            )}
          </>
        )}
      </Box>
    </Tooltip>
  );
};

const Main = () => {
  const [seList, setSeList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchTimeout = useRef(null);

  const [selectedSE, setSelectedSE] = useState(null);
  const [depotPanelOpen, setDepotPanelOpen] = useState(false);

  const [addDepotModal, setAddDepotModal] = useState(false);
  const [editDepotModal, setEditDepotModal] = useState(false);

  const [depotForm, setDepotForm] = useState(emptyDepotForm);
  const [formErrors, setFormErrors] = useState(emptyErrors);
  const [submitLoading, setSubmitLoading] = useState(false);

  const [checkResult, setCheckResult] = useState(null);
  const [checkLoading, setCheckLoading] = useState(false);

  const [editingDepot, setEditingDepot] = useState(null);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [depotToDelete, setDepotToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileSE, setProfileSE] = useState(null);
  const [addSEModalOpen, setAddSEModalOpen] = useState(false);

  const [assignTerritoryOpen, setAssignTerritoryOpen] = useState(false);
  const [assignTerritorySE, setAssignTerritorySE] = useState(null);

  const [territoryPanelOpen, setTerritoryPanelOpen] = useState(false);
  const [deleteTerritoryConfirmOpen, setDeleteTerritoryConfirmOpen] = useState(false);
  const [territoryToDelete, setTerritoryToDelete] = useState(null);
  const [deleteTerritoryLoading, setDeleteTerritoryLoading] = useState(false);

  const handleAssignTerritory = (se) => {
    setAssignTerritorySE(se);
    setAssignTerritoryOpen(true);
  };

  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const totalAssignedDepots = seList.reduce((count, se) => count + (se.depots?.length ?? 0), 0);
  const activeSECount = seList.filter((se) => se.isActive).length;
  const inactiveSECount = seList.length - activeSECount;
  const avgDepotsPerSE =
    seList.length > 0 ? (totalAssignedDepots / seList.length).toFixed(1) : "0";

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await getAllSEUsers({ page: currentPage, limit: 10, search: debouncedSearch });
        const newList = res?.data ?? [];
        setSeList(newList);
        setTotalPages(res?.pagination?.totalPages ?? 1);
        setSelectedSE((prev) => {
          if (!prev) return prev;
          return newList.find((s) => s.SAP_ID === prev.SAP_ID) || prev;
        });
      } catch {
        toastMessage("Failed to load SE list", "error");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentPage, debouncedSearch, refreshTrigger]);

  const handleRefresh = () => setRefreshTrigger(prev => prev + 1);

  const handlePageChange = (_, page) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    setCurrentPage(1);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setDebouncedSearch(val);
    }, 300);
  };

  const openDeleteConfirm = (depot) => {
    setDepotToDelete(depot);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteDepot = async () => {
    setDeleteLoading(true);
    try {
      const res = await deleteDepotFromSE(selectedSE.SAP_ID, depotToDelete.depotCode);
      if (res?.status === "success") {
        toastMessage(res.message ?? "Depot deleted successfully", "success");
        setDeleteConfirmOpen(false);
        setDepotToDelete(null);
        setSelectedSE((prev) => ({
          ...prev,
          depots: prev.depots.filter((d) => d.depotCode !== depotToDelete.depotCode),
        }));
        setSeList((prev) =>
          prev.map((se) =>
            se.SAP_ID === selectedSE.SAP_ID
              ? { ...se, depots: se.depots.filter((d) => d.depotCode !== depotToDelete.depotCode) }
              : se
          )
        );
      } else {
        toastMessage(res?.message ?? "Error deleting depot", "error");
      }
    } catch (error) {
      toastMessage(error?.response?.data?.message || "Failed to remove depot", "error");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteTerritory = async () => {
    if (!selectedSE || !territoryToDelete) return;
    setDeleteTerritoryLoading(true);
    try {
      await deleteTerritoryFromSE({ seSapId: selectedSE.SAP_ID, territorySapId: territoryToDelete.SAP_ID });
      toastMessage(`Territory ${territoryToDelete.SAP_ID} removed successfully`, "success");

      setSeList((prev) =>
        prev.map((se) =>
          se.SAP_ID === selectedSE.SAP_ID
            ? {
              ...se,
              territories: (se.territories || []).filter((t) => t.SAP_ID !== territoryToDelete.SAP_ID),
            }
            : se
        )
      );

      if (selectedSE.SAP_ID === profileSE?.SAP_ID) {
        setProfileSE((prev) => ({
          ...prev,
          territories: (prev?.territories || []).filter((t) => t.SAP_ID !== territoryToDelete.SAP_ID),
        }));
      }

      setSelectedSE((prev) => ({
        ...prev,
        territories: (prev.territories || []).filter((t) => t.SAP_ID !== territoryToDelete.SAP_ID),
      }));

      setDeleteTerritoryConfirmOpen(false);
      setTerritoryToDelete(null);
    } catch (error) {
      toastMessage(error?.response?.data?.message || "Failed to remove territory", "error");
    } finally {
      setDeleteTerritoryLoading(false);
    }
  };

  const handleSelectSE = (se) => {
    setSelectedSE(se);
    setDepotPanelOpen(true);
    setTerritoryPanelOpen(false);
  };

  const handleSelectSETerritories = (se) => {
    setSelectedSE(se);
    setTerritoryPanelOpen(true);
    setDepotPanelOpen(false);
  };

  const openProfile = (se) => {
    setProfileSE(se);
    setProfileModalOpen(true);
  };

  const closeProfile = () => {
    setProfileModalOpen(false);
    setProfileSE(null);
  };

  const handleProfileUpdated = (updated) => {
    setSeList((prev) =>
      prev.map((item) => (item.SAP_ID === updated.SAP_ID ? { ...item, ...updated } : item))
    );
    setSelectedSE((prev) => (prev?.SAP_ID === updated.SAP_ID ? { ...prev, ...updated } : prev));
    setProfileSE((prev) => (prev?.SAP_ID === updated.SAP_ID ? { ...prev, ...updated } : prev));
  };

  const closeDepotPanel = () => {
    setDepotPanelOpen(false);
    setSelectedSE(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    // regionCode must contain digits only — strip any non-numeric characters
    const sanitized = name === "regionCode" ? value.replace(/\D/g, "") : value;
    setDepotForm((prev) => ({ ...prev, [name]: sanitized }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
    if (name === "depotCode") setCheckResult(null);
  };

  const handleDepotCodeBlur = async () => {
    if (!depotForm.depotCode.trim()) return;
    setCheckLoading(true);
    try {
      const res = await checkDepotCode(depotForm.depotCode.trim());
      setCheckResult(res);
    } catch {
      // silently ignore — user can still submit
    } finally {
      setCheckLoading(false);
    }
  };

  const validateForm = () => {
    const errs = {};
    if (!depotForm.depotCode.trim()) errs.depotCode = "Depot Code is required";
    if (!depotForm.depotName.trim()) errs.depotName = "Depot Name is required";
    if (!depotForm.regionCode.trim()) errs.regionCode = "Region Code is required";
    if (!depotForm.regionName.trim()) errs.regionName = "Region Name is required";
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const openAddDepot = () => {
    setDepotForm(emptyDepotForm);
    setFormErrors(emptyErrors);
    setCheckResult(null);
    setAddDepotModal(true);
  };

  const openEditDepot = (depot) => {
    setEditingDepot(depot);
    setDepotForm({ ...depot });
    setFormErrors(emptyErrors);
    setEditDepotModal(true);
  };

  const handleAddDepot = async () => {
    if (!validateForm()) return;
    setSubmitLoading(true);
    try {
      const res = await addDepotToSE(selectedSE.SAP_ID, [{ ...depotForm }]);
      if (res?.status === "success") {
        const addedCodes = res.added ?? [];
        const skippedCodes = res.skipped ?? [];
        const msg = skippedCodes.length
          ? `${res.message} (skipped already-assigned: ${skippedCodes.join(", ")})`
          : (res.message ?? "Depot added successfully");
        toastMessage(msg, "success");
        setAddDepotModal(false);
        setDepotForm(emptyDepotForm);
        setCheckResult(null);
        if (addedCodes.length > 0) {
          const newDepot = { ...depotForm };
          setSelectedSE((prev) => ({ ...prev, depots: [...(prev.depots ?? []), newDepot] }));
          setSeList((prev) =>
            prev.map((se) =>
              se.SAP_ID === selectedSE.SAP_ID
                ? { ...se, depots: [...(se.depots ?? []), newDepot] }
                : se
            )
          );
        }
      } else if (res?.status === "error") {
        if (res.invalidItems?.length > 0) {
          const errs = {};
          res.invalidItems.forEach(({ missing }) => {
            missing.forEach((field) => {
              errs[field] = `${field} is required`;
            });
          });
          setFormErrors((prev) => ({ ...prev, ...errs }));
          toastMessage("Please fix the highlighted fields", "error");
        } else {
          toastMessage(res.message ?? "Error adding depot", "error");
        }
      }
    } catch (err) {
      toastMessage(err?.response?.data?.message ?? err?.message ?? "Failed to add depot", "error");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEditDepot = async () => {
    if (!validateForm()) return;
    setSubmitLoading(true);
    try {
      const payload = { seSapId: selectedSE.SAP_ID, depotCode: editingDepot.depotCode };
      if (depotForm.depotName !== editingDepot.depotName) payload.depotName = depotForm.depotName;
      if (depotForm.regionCode !== editingDepot.regionCode) payload.regionCode = depotForm.regionCode;
      if (depotForm.regionName !== editingDepot.regionName) payload.regionName = depotForm.regionName;

      const res = await editDepotOnSE(payload);
      if (res?.status === "success") {
        toastMessage(res.message ?? "Depot updated successfully", "success");
        setEditDepotModal(false);
        setEditingDepot(null);
        const updated = { ...depotForm };
        setSelectedSE((prev) => ({
          ...prev,
          depots: prev.depots.map((d) =>
            d.depotCode === editingDepot.depotCode ? updated : d
          ),
        }));
        setSeList((prev) =>
          prev.map((se) =>
            se.SAP_ID === selectedSE.SAP_ID
              ? {
                ...se,
                depots: se.depots.map((d) =>
                  d.depotCode === editingDepot.depotCode ? updated : d
                ),
              }
              : se
          )
        );
      } else {
        toastMessage(res?.message ?? "Error updating depot", "error");
      }
    } catch {
      toastMessage("Failed to update depot", "error");
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <>
      <PageHeader
        title="SE Depot Management"
        subtitle="Manage Sales Executive to depot assignments with centralized controls."
        action={
          <Button
            variant="contained"
            color="primary"
            startIcon={<PersonAddOutlinedIcon />}
            onClick={() => setAddSEModalOpen(true)}
            sx={{ textTransform: "none", fontWeight: 600, px: 2.5, flexShrink: 0 }}
          >
            Add SE to Master
          </Button>
        }
      />

      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            label="SE on this page"
            value={loading ? "—" : seList.length}
            icon={PersonOutlineIcon}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            label="Active executives"
            value={loading ? "—" : activeSECount}
            icon={CheckCircleIcon}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            label="Assigned depots"
            value={loading ? "—" : totalAssignedDepots}
            icon={WarehouseOutlinedIcon}
            color="info"
          />
        </Grid>
      </Grid>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2.5,
          border: "1px solid",
          borderColor: "divider",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { sm: "center" },
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Find Sales Executive
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Search by SAP ID · click a row to manage depots
          </Typography>
        </Box>
        <TextField
          size="small"
          placeholder="Search SAP ID"
          value={search}
          onChange={handleSearchChange}
          sx={{ minWidth: { xs: "100%", sm: 320 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {loading ? (
        <Paper sx={{ p: 2, border: "1px solid", borderColor: "divider" }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} height={52} sx={{ mb: 1 }} animation="wave" />
          ))}
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            mt: 2,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>SAP ID</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Region</TableCell>
                {/* <TableCell sx={{ fontWeight: 700 }}>Status</TableCell> */}
                <TableCell sx={{ fontWeight: 700 }}>Depots</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">
                  Profile
                </TableCell>
                <TableCell sx={{ fontWeight: 700, width: "240px" }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {seList.length > 0 ? (
                seList.map((se) => {
                  const isSelected = depotPanelOpen && selectedSE?.SAP_ID === se.SAP_ID;
                  const isRowActive = (depotPanelOpen || territoryPanelOpen) && selectedSE?.SAP_ID === se.SAP_ID;
                  return (
                    <TableRow
                      key={se.SAP_ID}
                      hover
                      onClick={() => handleSelectSE(se)}
                      sx={{
                        cursor: "pointer",
                        transition: "background-color 0.2s ease",
                        bgcolor: (theme) => (isRowActive ? alpha(theme.palette.primary.main, 0.08) : "inherit"),
                        borderLeft: "4px solid",
                        borderLeftColor: isRowActive ? "primary.main" : "transparent",
                        "&:last-child td, &:last-child th": { borderBottom: 0 },
                        "&:hover": { bgcolor: (theme) => (isRowActive ? alpha(theme.palette.primary.main, 0.12) : theme.palette.action.hover) },
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600, color: "primary.dark" }}>{se.SAP_ID}</TableCell>
                      <TableCell sx={{ color: "text.secondary" }}>{se.email}</TableCell>
                      <TableCell>{se.phone}</TableCell>
                      <TableCell>
                        {se.regionName} {se.regionCode}
                      </TableCell>
                      {/* <TableCell>
                      <Chip
                        label={se.isActive ? "Active" : "Inactive"}
                        color={se.isActive ? "success" : "default"}
                        size="small"
                      />
                    </TableCell> */}
                      <TableCell>
                        <AssignedDepotsHover depots={se.depots ?? []} />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View & edit profile">
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            startIcon={<VisibilityOutlinedIcon sx={{ fontSize: "16px !important" }} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              openProfile(se);
                            }}
                            sx={{ textTransform: "none", fontWeight: 600, whiteSpace: "nowrap" }}
                          >
                            Profile
                          </Button>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <Button
                            size="small"
                            variant={territoryPanelOpen && selectedSE?.SAP_ID === se.SAP_ID ? "contained" : "outlined"}
                            color="info"
                            endIcon={<OpenInNewOutlinedIcon sx={{ fontSize: "16px !important" }} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectSETerritories(se);
                            }}
                            sx={{ textTransform: "none", fontWeight: 600, whiteSpace: "nowrap" }}
                          >
                            Manage Territories
                          </Button>
                          <Button
                            size="small"
                            variant={depotPanelOpen && selectedSE?.SAP_ID === se.SAP_ID ? "contained" : "outlined"}
                            color="primary"
                            endIcon={<OpenInNewOutlinedIcon sx={{ fontSize: "16px !important" }} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectSE(se);
                            }}
                            sx={{ textTransform: "none", fontWeight: 600, whiteSpace: "nowrap" }}
                          >
                            Manage Depots
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={9} sx={{ borderBottom: 0 }}>
                    <EmptyState
                      icon={PersonOutlineIcon}
                      title="No SE users found"
                      description={debouncedSearch ? "Try a different SAP ID search." : "No records on this page."}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePager
            totalPages={totalPages}
            page={currentPage}
            rowsPerPage={10}
            onPageChange={(p) => handlePageChange(null, p)}
          />
        </TableContainer>
      )}

      <AddSEModal open={addSEModalOpen} onClose={() => setAddSEModalOpen(false)} onRefresh={handleRefresh} />
      <AssignTerritoryModal open={assignTerritoryOpen} onClose={() => setAssignTerritoryOpen(false)} seUser={assignTerritorySE} onRefresh={handleRefresh} />

      <SEProfileModal
        open={profileModalOpen}
        onClose={closeProfile}
        se={profileSE}
        onUpdated={handleProfileUpdated}
      />

      <ManageDepotsModal
        open={depotPanelOpen}
        onClose={closeDepotPanel}
        selectedSE={selectedSE}
        onAddDepot={openAddDepot}
        onEditDepot={openEditDepot}
        onDeleteDepot={openDeleteConfirm}
      />

      {/* Add Depot modal */}
      <Modal
        open={addDepotModal}
        onClose={() => {
          setAddDepotModal(false);
          setDepotForm(emptyDepotForm);
          setFormErrors(emptyErrors);
          setCheckResult(null);
        }}
        closeAfterTransition
        BackdropProps={{ sx: modalBackdropSx }}
      >
        <Fade in={addDepotModal}>
          <Box sx={modalStyle}>
            <ModalHeader
              icon={AddIcon}
              title="Add Depot"
              subtitle="Assign a new depot to this Sales Executive"
              onClose={() => {
                setAddDepotModal(false);
                setDepotForm(emptyDepotForm);
                setFormErrors(emptyErrors);
                setCheckResult(null);
              }}
            />
            <Box sx={{ ...modalBodySx, display: "flex", flexDirection: "column", gap: 2 }}>
              <Box>
                <TextField
                  fullWidth
                  label="Depot Code"
                  name="depotCode"
                  value={depotForm.depotCode}
                  onChange={handleFormChange}
                  onBlur={handleDepotCodeBlur}
                  error={!!formErrors.depotCode}
                  helperText={formErrors.depotCode}
                  InputProps={{
                    endAdornment: checkLoading ? (
                      <CircularProgress size={18} />
                    ) : checkResult?.status === "available" ? (
                      <CheckCircleIcon color="success" />
                    ) : null,
                  }}
                />
                {checkResult?.status === "available" && (
                  <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: "block" }}>
                    {checkResult.message}
                  </Typography>
                )}
                {checkResult?.status === "assigned" && (
                  <Alert severity="info" sx={{ mt: 0.5 }}>
                    Already held by{" "}
                    {checkResult.assignedTo
                      ?.map((se) => `(${se.SAP_ID})`)
                      .join(", ")}{" "}
                    — you can still add it
                  </Alert>
                )}
              </Box>
              <TextField
                fullWidth
                label="Depot Name"
                name="depotName"
                value={depotForm.depotName}
                onChange={handleFormChange}
                error={!!formErrors.depotName}
                helperText={formErrors.depotName}
              />
              <TextField
                fullWidth
                label="Region Code"
                name="regionCode"
                value={depotForm.regionCode}
                onChange={handleFormChange}
                error={!!formErrors.regionCode}
                helperText={formErrors.regionCode}
                inputProps={{ inputMode: "numeric" }}
              />
              <TextField
                fullWidth
                label="Region Name"
                name="regionName"
                value={depotForm.regionName}
                onChange={handleFormChange}
                error={!!formErrors.regionName}
                helperText={formErrors.regionName}
              />
            </Box>
            <Box sx={modalFooterSx}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => {
                  setAddDepotModal(false);
                  setDepotForm(emptyDepotForm);
                  setFormErrors(emptyErrors);
                  setCheckResult(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={submitLoading ? null : <AddIcon />}
                onClick={handleAddDepot}
                disabled={submitLoading}
                sx={{ minWidth: 130, textTransform: "none" }}
              >
                {submitLoading ? <CircularProgress size={20} color="inherit" /> : "Add Depot"}
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* Edit Depot modal */}
      <Modal
        open={editDepotModal}
        onClose={() => {
          setEditDepotModal(false);
          setDepotForm(emptyDepotForm);
          setFormErrors(emptyErrors);
          setEditingDepot(null);
        }}
        closeAfterTransition
        BackdropProps={{ sx: modalBackdropSx }}
      >
        <Fade in={editDepotModal}>
          <Box sx={modalStyle}>
            <ModalHeader
              icon={EditOutlinedIcon}
              title={`Edit Depot — ${editingDepot?.depotCode ?? ""}`}
              subtitle="Update the depot details for this Sales Executive"
              onClose={() => {
                setEditDepotModal(false);
                setDepotForm(emptyDepotForm);
                setFormErrors(emptyErrors);
                setEditingDepot(null);
              }}
            />
            <Box sx={{ ...modalBodySx, display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                fullWidth
                label="Depot Code"
                name="depotCode"
                value={depotForm.depotCode}
                disabled
              />
              <TextField
                fullWidth
                label="Depot Name"
                name="depotName"
                value={depotForm.depotName}
                onChange={handleFormChange}
                error={!!formErrors.depotName}
                helperText={formErrors.depotName}
              />
              <TextField
                fullWidth
                label="Region Code"
                name="regionCode"
                value={depotForm.regionCode}
                onChange={handleFormChange}
                error={!!formErrors.regionCode}
                helperText={formErrors.regionCode}
                inputProps={{ inputMode: "numeric" }}
              />
              <TextField
                fullWidth
                label="Region Name"
                name="regionName"
                value={depotForm.regionName}
                onChange={handleFormChange}
                error={!!formErrors.regionName}
                helperText={formErrors.regionName}
              />
            </Box>
            <Box sx={modalFooterSx}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => {
                  setEditDepotModal(false);
                  setDepotForm(emptyDepotForm);
                  setFormErrors(emptyErrors);
                  setEditingDepot(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={submitLoading ? null : <EditOutlinedIcon />}
                onClick={handleEditDepot}
                disabled={submitLoading}
                sx={{ minWidth: 140, textTransform: "none" }}
              >
                {submitLoading ? <CircularProgress size={20} color="inherit" /> : "Save Changes"}
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* Delete confirmation modal */}
      <Modal
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setDepotToDelete(null);
        }}
        closeAfterTransition
        BackdropProps={{ sx: modalBackdropSx }}
      >
        <Fade in={deleteConfirmOpen}>
          <Box sx={{ ...modalStyle, width: { xs: "92%", sm: "420px" } }}>
            <ModalHeader
              icon={WarningAmberOutlinedIcon}
              title="Delete Depot"
              subtitle="This action cannot be undone"
              onClose={() => {
                setDeleteConfirmOpen(false);
                setDepotToDelete(null);
              }}
              accent="error.main"
            />
            <Box sx={{ ...modalBodySx, textAlign: "center" }}>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                Remove the following depot from{" "}
                <strong>{selectedSE?.name}</strong> ({selectedSE?.SAP_ID})?
              </Typography>
              <Paper
                variant="outlined"
                sx={{ p: 2, bgcolor: (theme) => alpha(theme.palette.error.main, 0.08), borderColor: "error.light" }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {depotToDelete?.depotCode}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {depotToDelete?.depotName}
                </Typography>
              </Paper>
            </Box>
            <Box sx={modalFooterSx}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => {
                  setDeleteConfirmOpen(false);
                  setDepotToDelete(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={deleteLoading ? null : <DeleteIcon />}
                onClick={handleDeleteDepot}
                disabled={deleteLoading}
                sx={{ minWidth: 110, textTransform: "none" }}
              >
                {deleteLoading ? <CircularProgress size={20} color="inherit" /> : "Delete"}
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* Territories Side Panel */}
      <Drawer
        anchor="right"
        open={territoryPanelOpen}
        onClose={() => {
          setTerritoryPanelOpen(false);
          setSelectedSE(null);
        }}
        PaperProps={{ sx: { width: { xs: "100%", sm: 400 }, p: 0, bgcolor: "background.default" } }}
      >
        {selectedSE && (
          <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider", ...headerBandSx }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Manage Territories
                </Typography>
                <IconButton onClick={() => setTerritoryPanelOpen(false)} size="small" sx={{ bgcolor: "background.paper", "&:hover": { bgcolor: "action.hover" } }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
              <Paper variant="outlined" sx={{ p: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
                <Avatar sx={{ width: 44, height: 44, bgcolor: "info.main", fontWeight: 700 }}>
                  <PersonOutlineIcon />
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
                    Service Engineer
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    SAP ID: {selectedSE.SAP_ID}
                  </Typography>
                </Box>
                <Chip label={`${selectedSE.territories?.length || 0} assigned`} size="small" color="info" variant="outlined" sx={{ fontWeight: 700 }} />
              </Paper>
            </Box>

            <Box sx={{ p: 2, flex: 1, overflowY: "auto" }}>
              <Button
                fullWidth
                variant="outlined"
                color="info"
                startIcon={<AddIcon />}
                onClick={() => {
                  setAssignTerritorySE(selectedSE);
                  setAssignTerritoryOpen(true);
                }}
                sx={{ mb: 2, py: 1, borderStyle: "dashed", borderWidth: 2, "&:hover": { borderWidth: 2 } }}
              >
                Assign SO Territory
              </Button>

              <List sx={{ pt: 0 }}>
                {selectedSE.territories && selectedSE.territories.length > 0 ? (
                  selectedSE.territories.map((terr) => (
                    <Paper key={terr.SAP_ID} variant="outlined" sx={{ mb: 1.5, overflow: "hidden", transition: "box-shadow 0.2s", "&:hover": { boxShadow: 4 } }}>
                      <Box sx={{ p: 1.5, borderBottom: "1px solid", borderColor: "divider", display: "flex", justifyContent: "space-between", alignItems: "center", bgcolor: "background.default" }}>
                        <Chip label={terr.SAP_ID} size="small" color="info" sx={{ fontWeight: 700, fontSize: "0.75rem" }} />
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="Remove territory">
                            <IconButton size="small" color="error" onClick={() => {
                              setTerritoryToDelete(terr);
                              setDeleteTerritoryConfirmOpen(true);
                            }}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Box>
                      <Box sx={{ p: 1.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {terr.name || "N/A"}
                        </Typography>
                        <Stack spacing={0.5} sx={{ mt: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <PlaceOutlinedIcon sx={{ fontSize: 14 }} />
                            <b>Type:</b> {terr.territoryType || "N/A"} · <b>Code:</b> {terr.territoryCode || "N/A"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                            <b>Category:</b> {terr.territoryCategory || "N/A"} · <b>Position:</b> {terr.territoryPosition || "N/A"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <WarehouseOutlinedIcon sx={{ fontSize: 14 }} />
                            <b>Werks:</b> {terr.werks || "N/A"} · <b>Scope:</b> {terr.territoryScope || "N/A"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <ManageAccountsOutlinedIcon sx={{ fontSize: 14 }} />
                            <b>Group:</b> {terr.seTerrGrp || "N/A"}
                          </Typography>
                        </Stack>
                      </Box>
                    </Paper>
                  ))
                ) : (
                  <Box sx={{ textAlign: "center", py: 6 }}>
                    <PlaceOutlinedIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
                    <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 600 }}>
                      No territories assigned
                    </Typography>
                    <Typography variant="body2" color="text.disabled">
                      Click 'Assign SO Territory' to add one.
                    </Typography>
                  </Box>
                )}
              </List>
            </Box>
          </Box>
        )}
      </Drawer>

      {/* Delete Territory Confirmation Modal */}
      <Modal
        open={deleteTerritoryConfirmOpen}
        onClose={() => {
          setDeleteTerritoryConfirmOpen(false);
          setTerritoryToDelete(null);
        }}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ sx: modalBackdropSx }}
      >
        <Fade in={deleteTerritoryConfirmOpen}>
          <Box sx={{ ...modalStyle, width: { xs: "92%", sm: "420px" } }}>
            <ModalHeader
              icon={WarningAmberOutlinedIcon}
              title="Delete Territory"
              subtitle="This action cannot be undone"
              onClose={() => {
                setDeleteTerritoryConfirmOpen(false);
                setTerritoryToDelete(null);
              }}
              accent="error.main"
            />
            <Box sx={{ ...modalBodySx, textAlign: "center" }}>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                Remove the following territory from{" "}
                <strong>{selectedSE?.SAP_ID}</strong>?
              </Typography>
              <Paper
                variant="outlined"
                sx={{ p: 2, bgcolor: (theme) => alpha(theme.palette.error.main, 0.08), borderColor: "error.light" }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {territoryToDelete?.SAP_ID}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {territoryToDelete?.name || "N/A"}
                </Typography>
              </Paper>
            </Box>
            <Box sx={modalFooterSx}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => {
                  setDeleteTerritoryConfirmOpen(false);
                  setTerritoryToDelete(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={deleteTerritoryLoading ? null : <DeleteIcon />}
                onClick={handleDeleteTerritory}
                disabled={deleteTerritoryLoading}
                sx={{ minWidth: 110, textTransform: "none" }}
              >
                {deleteTerritoryLoading ? <CircularProgress size={20} color="inherit" /> : "Delete"}
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>
    </>
  );
};

const SEDepot = () => {
  return (
    <AccessController roles={["ADMIN", "ADMINSE"]}>
      <DashGridL2 Top={TopBar} Main={Main} />
      <Toaster />
    </AccessController>
  );
};

export default SEDepot;
